<template>
  <div class="ia-panel-page">
    <section class="ia-hero">
      <div>
        <p class="ia-kicker">Módulo interno</p>
        <h1>Coletor de Evidências IAN</h1>
        <p>Painel oficial para orquestrar coletas, acompanhar batch, consultar resultados e exportar artefatos.</p>
      </div>
      <div class="ia-panel-actions">
        <v-btn v-if="isFullAdmin" outlined color="secondary" @click="$router.push({ name: 'ia-coleta-evidencias.regras' })">
          Regras de Analise
        </v-btn>
        <v-btn outlined color="primary" @click="carregarTudo" :loading="loadingPainel || loadingResultados">
          Atualizar
        </v-btn>
        <v-btn color="primary" @click="exportarXlsx" :disabled="!resultados.resultados.length">
          Baixar XLSX
        </v-btn>
        <v-btn color="secondary" @click="exportarPdf" :disabled="!resultados.resultados.length">
          Gerar PDF
        </v-btn>
      </div>
    </section>

    <v-row>
      <v-col cols="12" md="3" v-for="card in resumoCards" :key="card.label">
        <v-card outlined class="metric-card">
          <v-card-text>
            <div class="text-overline mb-1">{{ card.label }}</div>
            <div class="text-h5">{{ card.value }}</div>
            <div class="caption grey--text text--darken-1">{{ card.caption }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-tabs v-model="abaAtiva" background-color="transparent" color="primary" show-arrows>
        <v-tab v-if="isFullAdmin">Execuções</v-tab>
        <v-tab v-if="isFullAdmin">Orquestração</v-tab>
        <v-tab v-if="isFullAdmin">Batch 3.0</v-tab>
        <v-tab>Resultados</v-tab>
      </v-tabs>

      <v-divider />

      <v-tabs-items v-model="abaAtiva">
        <v-tab-item v-if="isFullAdmin">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="2" v-for="card in decisaoCards" :key="card.label">
                  <v-card outlined class="fill-height">
                    <v-card-text>
                      <div class="text-overline mb-1">{{ card.label }}</div>
                      <div class="text-h5">{{ card.value }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-data-table
                :headers="headersExecucoes"
                :items="painel.execucoes"
                :loading="loadingPainel"
                class="elevation-0 mt-4"
                no-data-text="Nenhuma execução encontrada."
                :items-per-page="10"
                :footer-props="{ itemsPerPageText: 'Linhas por página' }"
              >
                <template v-slot:item.inicio="{ item }">{{ formatDate(item.inicio) }}</template>
                <template v-slot:item.fim="{ item }">{{ formatDate(item.fim) }}</template>
                <template v-slot:item.status="{ item }">
                  <v-chip small dark :color="statusColor(item.status)">{{ item.status }}</v-chip>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="isFullAdmin">
          <v-card flat>
            <v-card-text>
              <div class="section-title">
                <div>
                  <h2>Controle de Execução</h2>
                  <p>Monte o recorte, gere o comando e, quando quiser, execute pelo próprio painel.</p>
                </div>
              </div>

              <v-row>
                <v-col cols="12" md="2">
                  <v-select v-model="operacao.modo" :items="modosExecucao" item-text="text" item-value="value" label="Modo" outlined dense />
                </v-col>
                <v-col cols="12" md="2">
                  <v-select v-model="operacao.uf" :items="filtros.ufs" item-text="uf" item-value="uf" label="UF" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="operacao.municipios"
                    :items="municipiosOperacao"
                    item-text="municipio"
                    item-value="municipio"
                    label="Municípios"
                    multiple
                    chips
                    small-chips
                    clearable
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="operacao.codigosQuesitos"
                    :items="filtros.quesitos"
                    item-text="codigoQuesito"
                    item-value="codigoQuesito"
                    label="Quesitos"
                    multiple
                    chips
                    small-chips
                    clearable
                    outlined
                    dense
                  >
                    <template v-slot:prepend-item>
                      <v-list-item ripple @click="toggleTodosQuesitos('operacao')">
                        <v-list-item-action>
                          <v-checkbox :input-value="todosQuesitosSelecionados('operacao')" color="primary" readonly hide-details />
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>Selecionar todos</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2" />
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row class="mt-0">
                <v-col cols="12" md="4">
                  <v-select
                    v-model="operacao.statusAlvo"
                    :items="statusAlvoOptions"
                    label="Reexecutar apenas status"
                    multiple
                    chips
                    small-chips
                    clearable
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox v-model="operacao.somenteElegiveisIan" label="Somente elegíveis IAN" class="mt-0" hide-details />
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox v-model="operacao.pularYesHistorico" label="Preservar resultados Sim históricos" class="mt-0" hide-details />
                </v-col>
              </v-row>

              <div class="inline-actions">
                <v-btn outlined color="primary" @click="previewComando" :loading="loadingComando">Gerar comando</v-btn>
                <v-btn color="primary" @click="executarComando" :loading="loadingExecucao">Iniciar coleta</v-btn>
                <v-btn text @click="selecionarPendencias">Selecionar no / review / timeout</v-btn>
                <v-btn text @click="limparOperacao">Limpar</v-btn>
              </div>

              <v-alert v-if="comandoAtual" type="info" outlined class="mt-4">
                <strong>{{ comandoAtual.titulo }}</strong>
                <div>{{ comandoAtual.estimativa }}</div>
                <pre class="command-preview">{{ comandoAtual.comando }}</pre>
                <div v-for="aviso in comandoAtual.avisos" :key="aviso" class="caption">{{ aviso }}</div>
              </v-alert>

              <v-data-table
                :headers="headersProcessos"
                :items="processos"
                class="elevation-0 mt-4"
                no-data-text="Nenhum processo local iniciado pelo painel."
                :items-per-page="5"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip small dark :color="statusColor(item.status)">{{ item.status }}</v-chip>
                </template>
                <template v-slot:item.atualizadoEm="{ item }">{{ formatDate(item.atualizadoEm) }}</template>
                <template v-slot:item.acoes="{ item }">
                  <v-btn small text color="error" :disabled="item.status !== 'running'" @click="cancelarProcesso(item.id)">Cancelar</v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="isFullAdmin">
          <v-card flat>
            <v-card-text>
              <div class="section-title">
                <div>
                  <h2>Batch 3.0</h2>
                  <p>Planeje o lote com filtros, acompanhe etapas ja submetidas, sincronize status e solicite cancelamento.</p>
                </div>
                <v-btn outlined color="primary" @click="carregarBatchProjetos" :loading="loadingBatch">Atualizar lotes</v-btn>
              </div>

              <v-row>
                <v-col cols="12" md="2">
                  <v-select v-model="batch.modo" :items="modosBatch" item-text="text" item-value="value" label="Tipo de batch" outlined dense />
                </v-col>
                <v-col cols="12" md="2">
                  <v-select v-model="batch.uf" :items="filtros.ufs" item-text="uf" item-value="uf" label="UF" outlined dense />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="batch.municipios"
                    :items="municipiosBatch"
                    item-text="municipio"
                    item-value="municipio"
                    label="Municípios"
                    multiple
                    chips
                    small-chips
                    clearable
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="batch.codigosQuesitos"
                    :items="filtros.quesitos"
                    item-text="codigoQuesito"
                    item-value="codigoQuesito"
                    label="Quesitos"
                    multiple
                    chips
                    small-chips
                    clearable
                    outlined
                    dense
                  >
                    <template v-slot:prepend-item>
                      <v-list-item ripple @click="toggleTodosQuesitos('batch')">
                        <v-list-item-action>
                          <v-checkbox :input-value="todosQuesitosSelecionados('batch')" color="primary" readonly hide-details />
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>Selecionar todos</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2" />
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="2">
                  <v-checkbox v-model="batch.somenteElegiveisIan" label="Elegíveis IAN" class="mt-0" hide-details />
                </v-col>
              </v-row>

              <v-row class="mt-0">
                <v-col cols="12" md="5">
                  <v-select
                    v-model="batch.statusAlvo"
                    :items="statusAlvoOptions"
                    label="Entrar no lote somente com status"
                    multiple
                    chips
                    small-chips
                    clearable
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox v-model="batch.pularYesHistorico" label="Não enviar itens com Sim histórico" class="mt-0" hide-details />
                </v-col>
                <v-col cols="12" md="3" class="d-flex justify-end">
                  <v-btn color="primary" @click="criarBatchProjeto" :loading="loadingCriarBatch">{{ batchButtonLabel }}</v-btn>
                </v-col>
              </v-row>

              <v-alert dense outlined type="info" class="mb-4">
                O painel cria o projeto, envia a busca da primeira rodada ao Batch API, sincroniza as etapas e avança para validação/consolidação quando a etapa anterior termina.
              </v-alert>

              <v-data-table
                :headers="headersBatch"
                :items="batchProjetos"
                :loading="loadingBatch"
                class="elevation-0 mt-4"
                no-data-text="Nenhum projeto batch encontrado."
                :items-per-page="10"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip small dark :color="statusColor(item.status)">{{ item.status }}</v-chip>
                </template>
                <template v-slot:item.percentual="{ item }">
                  <div class="progress-cell">
                    <v-progress-linear :value="item.percentual" height="8" rounded />
                    <span>{{ item.percentual }}%</span>
                  </div>
                </template>
                <template v-slot:item.updatedAt="{ item }">{{ formatDate(item.updatedAt) }}</template>
                <template v-slot:item.acoes="{ item }">
                  <v-btn small text @click="syncBatchProjeto(item.id)">Sync</v-btn>
                  <v-btn small text color="error" :disabled="item.status === 'completed'" @click="cancelarBatchProjeto(item.id)">Cancelar</v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="2">
                  <v-select v-model="filtro.uf" :items="filtros.ufs" item-text="uf" item-value="uf" label="UF" clearable outlined dense />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete v-model="filtro.municipio" :items="municipiosFiltrados" item-text="municipio" item-value="municipio" label="Município" clearable outlined dense />
                </v-col>
                <v-col cols="12" md="2">
                  <v-select v-model="filtro.codigoQuesito" :items="filtros.quesitos" item-text="codigoQuesito" item-value="codigoQuesito" label="Quesito" clearable outlined dense />
                </v-col>
                <v-col cols="12" md="2">
                  <v-select v-model="filtro.decisaoFinal" :items="decisoes" label="Decisão Final" clearable outlined dense />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="filtro.iaExecucaoId" :items="filtros.execucoes" item-text="nome" item-value="iaExecucaoId" label="Execução" clearable outlined dense />
                </v-col>
              </v-row>

              <div class="inline-actions">
                <v-checkbox v-model="filtro.somenteElegiveisIan" label="Somente elegíveis IAN" class="mt-0" hide-details />
                <v-spacer />
                <v-btn text @click="limparFiltros">Limpar filtros</v-btn>
                <v-btn color="primary" @click="aplicarFiltros" :loading="loadingResultados || loadingPainel">Aplicar filtros</v-btn>
              </div>

              <v-row class="mt-2">
                <v-col cols="12" md="2" v-for="card in resultadoCards" :key="card.label">
                  <v-card outlined>
                    <v-card-text>
                      <div class="text-overline mb-1">{{ card.label }}</div>
                      <div class="text-h5">{{ card.value }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-data-table
                :headers="headersResultados"
                :items="resultados.resultados"
                :loading="loadingResultados"
                class="elevation-0 mt-4 results-table"
                no-data-text="Nenhum resultado encontrado."
                :items-per-page="25"
                :footer-props="{ itemsPerPageText: 'Linhas por página' }"
              >
                <template v-slot:item.decisaoFinal="{ item }">
                  <v-chip small dark :color="statusColor(item.statusFinal)">{{ item.decisaoFinal }}</v-chip>
                </template>
                <template v-slot:item.comentarioPrincipal="{ item }">
                  <div class="text-body-2" :title="item.comentarioPrincipal || ''">{{ truncar(item.comentarioPrincipal, 160) }}</div>
                </template>
                <template v-slot:item.evidencia="{ item }">
                  <div v-if="evidenciasDoResultado(item).length" class="artifact-links">
                    <artifact-link
                      v-for="(artifact, index) in evidenciasDoResultado(item)"
                      :key="`${artifact.url || artifact.arquivoId}-${index}`"
                      :artifact="artifact"
                    />
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-slot:item.timeline="{ item }"><artifact-link :artifact="item.timeline" empty="-" /></template>
                <template v-slot:item.comentarios="{ item }"><artifact-link :artifact="item.comentarios" empty="-" /></template>
                <template v-slot:item.atualizadoEm="{ item }">{{ formatDate(item.atualizadoEm) }}</template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import { iaColetaEvidenciasApi, isApiArtifactUrl, openApiArtifact, resolveApiUrl } from "../services/api";
import { exportResultadosPdf, exportResultadosXlsx } from "../services/export";
import { atualUfDefinition } from "../../../helpers/utils";
import { mapGetters } from "vuex";

const ArtifactLink = {
  functional: true,
  props: {
    artifact: Object,
    empty: String,
  },
  render(h, context) {
    const artifact = context.props.artifact;
    if (!artifact || !artifact.url) return h("span", context.props.empty || "-");
    const data = { attrs: { href: resolveApiUrl(artifact.url), target: "_blank", rel: "noopener" } };

    if (isApiArtifactUrl(artifact.url)) {
      data.on = {
        click(event) {
          event.preventDefault();
          openApiArtifact(artifact.url);
        },
      };
    }

    return h(
      "a",
      data,
      artifact.rotulo || "Abrir"
    );
  },
};

export default {
  name: "IaColetaEvidenciasPainel",
  components: { ArtifactLink },
  data() {
    const ufAtual = this.resolveUfAtual();
    return {
      abaAtiva: 0,
      timerProcessos: null,
      loadingPainel: false,
      loadingResultados: false,
      loadingComando: false,
      loadingExecucao: false,
      loadingBatch: false,
      loadingCriarBatch: false,
      comandoAtual: null,
      processos: [],
      batchProjetos: [],
      painel: {
        resumo: { totalExecucoes: 0, totalMunicipios: 0, totalEvidencias: 0, ultimaAtualizacao: null },
        execucoes: [],
        consolidado: { total: 0, yes: 0, no: 0, review: 0, timeout: 0, error: 0, pending: 0 },
      },
      filtros: { ufs: [], municipios: [], quesitos: [], execucoes: [] },
      filtro: { uf: ufAtual, municipio: null, codigoQuesito: null, iaExecucaoId: null, decisaoFinal: null, somenteElegiveisIan: false },
      operacao: { modo: "rerun", uf: ufAtual, municipios: [], codigosQuesitos: [], statusAlvo: [], somenteElegiveisIan: false, pularYesHistorico: true, timeoutMs: 300000 },
      batch: { modo: "batch", uf: ufAtual, municipios: [], codigosQuesitos: [], statusAlvo: [], somenteElegiveisIan: false, pularYesHistorico: true, timeoutMs: 300000 },
      decisoes: ["Sim", "Não", "Revisão", "Timeout", "Erro", "Pendente"],
      statusAlvoOptions: ["no", "review", "timeout", "error"],
      modosExecucao: [
        { text: "Initial", value: "initial" },
        { text: "Rerun pendências", value: "rerun" },
        { text: "Redo selecionado", value: "redo" },
      ],
      modosBatch: [
        { text: "Batch 3.0 normal", value: "batch" },
        { text: "Batch intenso", value: "batch-intenso" },
        { text: "Batch super intenso", value: "batch-super-intenso" },
        { text: "Source chase pós-super intenso", value: "batch-source-chase" },
        { text: "Busca Pós Análise", value: "batch-pos-analise" },
      ],
      resultados: { resumo: { total: 0, yes: 0, no: 0, review: 0, timeout: 0, error: 0, pending: 0 }, resultados: [] },
      headersExecucoes: [
        { text: "ID", value: "iaExecucaoId" },
        { text: "Nome", value: "nome" },
        { text: "Status", value: "status" },
        { text: "Ambiente", value: "ambiente" },
        { text: "Municípios", value: "totalMunicipios" },
        { text: "Itens", value: "totalItens" },
        { text: "Início", value: "inicio" },
        { text: "Fim", value: "fim" },
      ],
      headersProcessos: [
        { text: "ID", value: "id" },
        { text: "Modo", value: "modo" },
        { text: "Status", value: "status" },
        { text: "Atualizado", value: "atualizadoEm" },
        { text: "Ações", value: "acoes", sortable: false },
      ],
      headersBatch: [
        { text: "Projeto", value: "id" },
        { text: "Status", value: "status" },
        { text: "UF", value: "uf" },
        { text: "Municípios", value: "totalMunicipios" },
        { text: "Itens", value: "plannedTasks" },
        { text: "Progresso", value: "percentual" },
        { text: "Etapa", value: "currentStageLabel" },
        { text: "Atualizado", value: "updatedAt" },
        { text: "Ações", value: "acoes", sortable: false },
      ],
      headersResultados: [
        { text: "UF", value: "uf" },
        { text: "Código IBGE", value: "codigoIbge" },
        { text: "Município", value: "municipio" },
        { text: "Quesito", value: "codigoQuesito" },
        { text: "Tema / Quesito", value: "temaPergunta" },
        { text: "Decisão Final", value: "decisaoFinal" },
        { text: "Evidência", value: "evidencia", sortable: false },
        { text: "Timeline", value: "timeline", sortable: false },
        { text: "Comentários", value: "comentarios", sortable: false },
        { text: "Comentário Principal", value: "comentarioPrincipal" },
        { text: "Atualizado Em", value: "atualizadoEm" },
      ],
    };
  },
  computed: {
    ...mapGetters(["isFullAdmin"]),
    resumoCards() {
      return [
        { label: "Execuções", value: this.painel.resumo.totalExecucoes, caption: "Histórico registrado" },
        { label: "Municípios", value: this.painel.resumo.totalMunicipios, caption: "Base consolidada" },
        { label: "Evidências", value: this.painel.resumo.totalEvidencias, caption: "Itens estruturados" },
        { label: "Última atualização", value: this.formatDate(this.painel.resumo.ultimaAtualizacao) || "-", caption: "Banco atual" },
      ];
    },
    decisaoCards() {
      return [
        { label: "Sim", value: this.painel.consolidado.yes },
        { label: "Não", value: this.painel.consolidado.no },
        { label: "Revisão", value: this.painel.consolidado.review },
        { label: "Timeout", value: this.painel.consolidado.timeout },
        { label: "Erro", value: this.painel.consolidado.error },
        { label: "Pendente", value: this.painel.consolidado.pending },
      ];
    },
    resultadoCards() {
      return [
        { label: "Total", value: this.resultados.resumo.total },
        { label: "Sim", value: this.resultados.resumo.yes },
        { label: "Não", value: this.resultados.resumo.no },
        { label: "Revisão", value: this.resultados.resumo.review },
        { label: "Timeout", value: this.resultados.resumo.timeout },
        { label: "Erro", value: this.resultados.resumo.error },
      ];
    },
    municipiosFiltrados() {
      return this.filterMunicipios(this.filtro.uf);
    },
    municipiosOperacao() {
      return this.filterMunicipios(this.operacao.uf);
    },
    municipiosBatch() {
      return this.filterMunicipios(this.batch.uf);
    },
    batchButtonLabel() {
      if (this.batch.modo === "batch-source-chase") return "Criar source chase";
      if (this.batch.modo === "batch-pos-analise") return "Criar busca pós análise";
      if (this.batch.modo === "batch-super-intenso") return "Criar batch super intenso";
      if (this.batch.modo === "batch-intenso") return "Criar batch intenso";
      return "Criar e enviar batch 3.0";
    },
  },
  watch: {
    "operacao.uf"() {
      this.operacao.municipios = [];
    },
    "batch.uf"() {
      this.batch.municipios = [];
    },
    "batch.modo"(modo) {
      if (modo === "batch-source-chase") {
        this.batch.statusAlvo = ["review"];
        return;
      }
      if (modo === "batch-pos-analise") {
        this.batch.statusAlvo = ["no", "review"];
        return;
      }
      if (modo === "batch-super-intenso" && !this.batch.statusAlvo.length) {
        this.batch.statusAlvo = ["no", "review"];
      }
    },
  },
  async created() {
    await this.carregarTudo();
    if (this.isFullAdmin) {
      this.timerProcessos = setInterval(() => {
        this.carregarProcessos();
        this.carregarBatchProjetos();
      }, 15000);
    }
  },
  beforeDestroy() {
    if (this.timerProcessos) clearInterval(this.timerProcessos);
  },
  methods: {
    resolveUfAtual() {
      const params = new URLSearchParams(window.location.search || "");
      const ufParam = params.get("uf");
      if (ufParam) {
        localStorage.setItem("iaColetaUf", ufParam.toUpperCase());
        return ufParam.toUpperCase();
      }

      const banco = (atualUfDefinition && atualUfDefinition.banco) || localStorage.getItem("iaColetaBanco") || "";
      if (banco.includes("-ms-")) return "MS";

      if (atualUfDefinition && atualUfDefinition.uf) return atualUfDefinition.uf;

      const ufStorage = localStorage.getItem("iaColetaUf");
      if (ufStorage) return ufStorage;

      return "RJ";
    },
    async carregarTudo() {
      const carregamentos = [this.carregarPainel(), this.carregarFiltros()];
      if (this.isFullAdmin) {
        carregamentos.push(this.carregarProcessos(), this.carregarBatchProjetos());
      }
      await Promise.allSettled(carregamentos);
      await this.carregarResultados();
    },
    async carregarPainel() {
      this.loadingPainel = true;
      try {
        this.painel = await iaColetaEvidenciasApi.obterPainel({ uf: this.filtro.uf || this.resolveUfAtual() });
      } finally {
        this.loadingPainel = false;
      }
    },
    async carregarFiltros() {
      this.filtros = await iaColetaEvidenciasApi.obterFiltros();
      const ufAtual = this.resolveUfAtual();
      if (this.filtros.ufs.some((item) => item.uf === ufAtual)) {
        this.filtro.uf = this.filtro.uf || ufAtual;
        this.operacao.uf = this.operacao.uf || ufAtual;
        this.batch.uf = this.batch.uf || ufAtual;
      }
    },
    async carregarResultados() {
      this.loadingResultados = true;
      try {
        const resultados = await iaColetaEvidenciasApi.obterResultados(this.filtro);
        resultados.resultados = (resultados.resultados || []).map((item) => ({
          ...item,
          temaPergunta: this.montarTemaPergunta(item),
        }));
        this.resultados = resultados;
        this.sincronizarPainelComUfAtual();
      } finally {
        this.loadingResultados = false;
      }
    },
    async aplicarFiltros() {
      await Promise.all([this.carregarPainel(), this.carregarResultados()]);
      this.sincronizarPainelComUfAtual();
    },
    sincronizarPainelComUfAtual() {
      if (!this.painel || !this.painel.resumo) return;

      const resumoResultados = this.resultados && this.resultados.resumo;
      if (resumoResultados) {
        this.painel = {
          ...this.painel,
          resumo: {
            ...this.painel.resumo,
            totalMunicipios: this.contarMunicipiosResultados(this.resultados.resultados),
            totalEvidencias: resumoResultados.total,
          },
          consolidado: {
            ...this.painel.consolidado,
            ...resumoResultados,
          },
        };
      }

      const ufAtual = (this.filtro.uf || this.resolveUfAtual() || "").toUpperCase();
      const totalExecucoesUf = (this.batchProjetos || []).filter((item) => (item.uf || "").toUpperCase() === ufAtual).length;
      if (totalExecucoesUf > 0) {
        this.painel = {
          ...this.painel,
          resumo: {
            ...this.painel.resumo,
            totalExecucoes: totalExecucoesUf,
          },
        };
      }
    },
    contarMunicipiosResultados(resultados) {
      return new Set((resultados || []).map((item) => {
        const uf = (item.uf || this.filtro.uf || "").toUpperCase();
        const codigo = item.codigoIbge || item.municipio || "";
        return `${uf}|${codigo}`;
      }).filter((item) => item !== "|")).size;
    },
    async carregarProcessos() {
      const data = await iaColetaEvidenciasApi.listarProcessos();
      this.processos = data.processos || [];
    },
    async carregarBatchProjetos() {
      this.loadingBatch = true;
      try {
        const data = await iaColetaEvidenciasApi.listarBatchProjetos();
        this.batchProjetos = data.projetos || [];
        this.sincronizarPainelComUfAtual();
      } finally {
        this.loadingBatch = false;
      }
    },
    async previewComando() {
      this.loadingComando = true;
      try {
        this.comandoAtual = await iaColetaEvidenciasApi.previewComando(this.operacao);
      } finally {
        this.loadingComando = false;
      }
    },
    async executarComando() {
      await this.previewComando();
      if (!window.confirm("Iniciar a coleta agora? Isso pode consumir API e levar tempo.")) return;
      this.loadingExecucao = true;
      try {
        await iaColetaEvidenciasApi.executarComando(this.operacao);
        await this.carregarProcessos();
      } finally {
        this.loadingExecucao = false;
      }
    },
    async cancelarProcesso(id) {
      await iaColetaEvidenciasApi.cancelarProcesso(id);
      await this.carregarProcessos();
    },
    async criarBatchProjeto() {
      if (!window.confirm(`Criar e enviar ${this.batchButtonLabel.toLowerCase()} real para o recorte selecionado?`)) return;
      this.loadingCriarBatch = true;
      try {
        await iaColetaEvidenciasApi.criarBatchProjeto(this.batch);
        await this.carregarBatchProjetos();
      } finally {
        this.loadingCriarBatch = false;
      }
    },
    async syncBatchProjeto(id) {
      await iaColetaEvidenciasApi.syncBatchProjeto(id);
      await this.carregarBatchProjetos();
    },
    async cancelarBatchProjeto(id) {
      if (!window.confirm("Solicitar cancelamento deste batch?")) return;
      await iaColetaEvidenciasApi.cancelarBatchProjeto(id);
      await this.carregarBatchProjetos();
    },
    selecionarPendencias() {
      this.operacao.statusAlvo = ["no", "review", "timeout", "error"];
      this.operacao.modo = "rerun";
    },
    limparOperacao() {
      this.operacao = { modo: "rerun", uf: this.operacao.uf || this.resolveUfAtual(), municipios: [], codigosQuesitos: [], statusAlvo: [], somenteElegiveisIan: false, pularYesHistorico: true, timeoutMs: 300000 };
      this.comandoAtual = null;
    },
    limparFiltros() {
      this.filtro = { uf: this.resolveUfAtual(), municipio: null, codigoQuesito: null, iaExecucaoId: null, decisaoFinal: null, somenteElegiveisIan: false };
      this.aplicarFiltros();
    },
    exportarXlsx() {
      exportResultadosXlsx(this.resultados.resultados);
    },
    exportarPdf() {
      exportResultadosPdf(this.resultados.resultados, this.filtro, this.resultados.resumo);
    },
    filterMunicipios(uf) {
      if (!uf) return this.filtros.municipios;
      return this.filtros.municipios.filter((item) => item.uf === uf);
    },
    getCodigosQuesitosDisponiveis() {
      return (this.filtros.quesitos || [])
        .map((item) => item.codigoQuesito)
        .filter(Boolean);
    },
    todosQuesitosSelecionados(contexto) {
      const codigos = this.getCodigosQuesitosDisponiveis();
      if (!codigos.length || !this[contexto]) return false;

      const selecionados = new Set((this[contexto].codigosQuesitos || []).map((codigo) => String(codigo)));
      return codigos.every((codigo) => selecionados.has(String(codigo)));
    },
    toggleTodosQuesitos(contexto) {
      if (!this[contexto]) return;

      const codigos = this.getCodigosQuesitosDisponiveis();
      this[contexto].codigosQuesitos = this.todosQuesitosSelecionados(contexto) ? [] : codigos.slice();
    },
    statusColor(status) {
      const normalized = String(status || "").toLowerCase();
      if (normalized === "yes" || normalized === "sim" || normalized === "completed") return "green";
      if (normalized === "running" || normalized === "in_progress") return "blue";
      if (normalized === "planned" || normalized === "queued") return "blue-grey";
      if (normalized === "review" || normalized === "revisão") return "orange";
      if (normalized === "timeout" || normalized === "cancelled" || normalized === "cancel_requested") return "deep-orange";
      if (normalized === "error" || normalized === "erro" || normalized === "failed") return "red";
      return "grey";
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    evidenciasDoResultado(item) {
      if (item && Array.isArray(item.evidencias) && item.evidencias.length) {
        return item.evidencias;
      }
      return item && item.evidencia ? [item.evidencia] : [];
    },
    montarTemaPergunta(item) {
      const tema = String((item && item.tema) || "").trim();
      const pergunta = String((item && item.pergunta) || "").trim();
      if (tema && pergunta) return `${tema} - ${pergunta}`;
      return tema || pergunta;
    },
    truncar(texto, limite) {
      if (!texto) return "";
      return texto.length > limite ? `${texto.slice(0, limite)}...` : texto;
    },
  },
};
</script>

<style scoped>
.ia-panel-page {
  padding: 104px 24px 40px;
}

.ia-hero {
  align-items: flex-start;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.artifact-links {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ia-hero h1 {
  font-size: 30px;
  font-weight: 800;
  margin: 0 0 8px;
}

.ia-hero p {
  color: #666;
  margin: 0;
}

.ia-kicker {
  color: #c45100 !important;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.22em;
  margin-bottom: 6px !important;
  text-transform: uppercase;
}

.ia-panel-actions,
.inline-actions,
.section-title {
  align-items: center;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.ia-panel-actions {
  justify-content: flex-end;
}

.section-title {
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-title h2 {
  font-size: 20px;
  font-weight: 800;
  margin: 0 0 4px;
}

.section-title p {
  color: #666;
  margin: 0;
}

.metric-card {
  min-height: 104px;
}

.command-preview {
  background: #101820;
  border-radius: 8px;
  color: #f8f8f2;
  font-size: 12px;
  margin: 10px 0 0;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
}

.progress-cell {
  align-items: center;
  display: grid;
  gap: 6px;
  min-width: 130px;
}

.results-table {
  min-width: 100%;
}

@media (max-width: 900px) {
  .ia-panel-page {
    padding: 96px 12px 32px;
  }

  .ia-hero {
    display: block;
  }

  .ia-panel-actions {
    justify-content: stretch;
    margin-top: 16px;
  }

  .ia-panel-actions .v-btn {
    flex: 1 1 100%;
  }
}
</style>
